import type React from 'react';
import { useTranslation } from 'react-i18next';

import ChartLineFill from '@/assets/icons/chart_line_fill.svg?component';
import FirstAidKitFill from '@/assets/icons/first_aid_kit_fill.svg?component';
import Louse from '@/assets/icons/louse.svg?component';
import { useChartTheme } from '@/components/charts/theme';
import { graphSettings } from '@/config/settings';
import { hasWoundScoringPen } from '@/domain/cages/speciesTraits';
import { type OverviewLocation, summariseOverviewSpecies } from '@/domain/client';
import type { LocationsLicenseOverview } from '@/domain/licenses';
import {
    formatGrowthUnit,
    formatGrowthValue,
    formatLiceValue,
    formatWoundsValue,
} from '@/utils/localeFormatters';

import MetricCard from '../MetricCard/MetricCard';

import styles from './AverageHeader.module.scss';

export interface AverageHeaderProps {
    children?: React.ReactNode;
    location: OverviewLocation;
    locationLicense?: LocationsLicenseOverview;
    isOptoScaleAdmin?: boolean;
}

function AverageHeader({ location, locationLicense, isOptoScaleAdmin }: AverageHeaderProps) {
    const { t, i18n } = useTranslation();
    const hasAccessToFishHealth =
        isOptoScaleAdmin || (locationLicense?.licenses.fishHealthLicense.hasValidLicense ?? false);
    const hasAccessToLice =
        isOptoScaleAdmin || (locationLicense?.licenses.liceLicense.hasValidLicense ?? false);
    // The heading colour keys off a species, so it needs a single one: a mixed site falls
    // back to the site default rather than picking one of its pens' species (WB-2207).
    const species = summariseOverviewSpecies(location.cages);
    const colorKey = (species.single ?? location.locationFishType)?.shortName;
    // The species colours are chart palette colours; trout's navy needs its dark lift.
    const chartTheme = useChartTheme();
    const speciesColor = colorKey ? graphSettings.fishTypeName[colorKey] : undefined;
    const color = speciesColor ? chartTheme.color(speciesColor) : undefined;

    return (
        <div className={`${styles.averageheader}`} style={{ color: color }}>
            <MetricCard
                value={formatGrowthValue(location.locationAverages.growth, i18n.language)}
                suffix={formatGrowthUnit(t('daypartTag.day'))}
                title={t('common.labels.growth')}
                icon={ChartLineFill}
                tone="info"
                framed="tinted"
            />

            {hasAccessToLice ? (
                <MetricCard
                    value={formatLiceValue(location.locationAverages.femaleLice, i18n.language)}
                    title={t('common.labels.lice')}
                    icon={Louse}
                    tone="warning"
                    framed="tinted"
                />
            ) : (
                ''
            )}

            {/* One pen with wound scoring is enough to make the average meaningful, whatever
                the site default says. A trait rather than a species check, so a species added
                later keeps it. */}
            {hasAccessToFishHealth && hasWoundScoringPen(location.cages) ? (
                <MetricCard
                    value={formatWoundsValue(location?.locationAverages?.wounds, i18n.language)}
                    suffix="%"
                    title={t('common.labels.wound')}
                    icon={FirstAidKitFill}
                    tone="success"
                    framed="tinted"
                />
            ) : (
                ''
            )}
        </div>
    );
}

export default AverageHeader;
export { AverageHeader };
